import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { DashboardLayout } from '../../components/dashboard';
import { StakeholderForm } from '../../components/stakeholders/StakeholderForm';
import { stakeholderService } from '../../services/stakeholderService';
import { CreateStakeholderDto } from '../../types/stakeholder';
import { assignmentService } from '../../services/assignmentService';
import { mediaService } from '../../services/mediaService';
import { upsertInfluenceScores } from '../../services/influencesService';
import { businessCardService, StakeholderMatch } from '../../services/businessCardService';
import { ChevronLeft, ScanLine, X } from 'lucide-react';
import { BusinessCardImport } from '../../components/stakeholders/BusinessCardImport';
import { DuplicateReviewSheet } from '../../components/stakeholders/DuplicateReviewSheet';
import { useToast } from '../../components/common';
import { Button, IconButton, Page, PageHeader } from '../../components/ui';

export const StakeholderCreatePage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { showSuccess, showError, showWarning } = useToast();

  const [scores, setScores] = useState<{ categoryId: string; score: number }[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [prefillData, setPrefillData] = useState<any>(null);
  const [organizationId, setOrganizationId] = useState<string | null>(null);
  const [isNewOrganization, setIsNewOrganization] = useState(false);
  const [organizationName, setOrganizationName] = useState<string>('');
  const [showImport, setShowImport] = useState(false);
  const [showPrefillBanner, setShowPrefillBanner] = useState(false);
  const [isDirty, setIsDirty] = useState(false);

  // Duplicate detection state for manual creation
  const [showDuplicateReview, setShowDuplicateReview] = useState(false);
  const [duplicateMatches, setDuplicateMatches] = useState<StakeholderMatch[]>([]);
  const [pendingCreateData, setPendingCreateData] = useState<{
    data: Partial<CreateStakeholderDto>;
    assignment?: any;
    extras?: any;
  } | null>(null);

  // Handle pre-filled data from navigation state
  useEffect(() => {
    if (location.state) {
      const { prefillData, organizationId, isNewOrganization, organizationName } = location.state as any;
      if (prefillData) {
        setPrefillData(prefillData);
        setOrganizationId(organizationId);
        setIsNewOrganization(isNewOrganization);
        setOrganizationName(organizationName || '');
        setShowPrefillBanner(true);
      }
    }
  }, [location.state]);

  // Warn before closing with unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isDirty) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isDirty]);

  // Stable identity: a new object on every render made the form re-apply the card data and
  // wipe the user's first edit (e.g. the category had to be picked twice)
  const formPrefill = useMemo(
    () => (prefillData ? { ...prefillData, organizationId, isNewOrganization, organizationName } : null),
    [prefillData, organizationId, isNewOrganization, organizationName],
  );

  const handleImport = (stakeholderData: any) => {
    setPrefillData(stakeholderData);
    setOrganizationId(null);
    setIsNewOrganization(false);
    setOrganizationName(stakeholderData.organizationName || '');
    setShowImport(false);
    setShowPrefillBanner(true);
    showSuccess('Data Extracted', 'Form pre-filled from business card. Review and adjust below.');
  };

  // Core creation logic (called after duplicate check passes)
  const executeCreate = useCallback(async (
    data: Partial<CreateStakeholderDto>,
    assignment?: { organizationId?: string; role?: string; department?: string; startDate?: string; endDate?: string; isCurrent?: boolean },
    extras?: { profileImageFile?: File | null; profileImageId?: string }
  ) => {
    setSubmitting(true);
    try {
      // Step 1: Create stakeholder
      const payload: CreateStakeholderDto = {
        firstName: (data.firstName ?? '').trim(),
        lastName: (data.lastName ?? '').trim(),
        email: data.email || undefined,
        phone: data.phone || undefined,
        professionalEmail: (data as any).professionalEmail || undefined,
        personalEmail: (data as any).personalEmail || undefined,
        professionalPhone: (data as any).professionalPhone || undefined,
        personalPhone: (data as any).personalPhone || undefined,
        facebookUrl: (data as any).facebookUrl || undefined,
        linkedinUrl: (data as any).linkedinUrl || undefined,
        twitterUrl: (data as any).twitterUrl || undefined,
        telegramUrl: (data as any).telegramUrl || undefined,
        whatsapp: (data as any).whatsapp || undefined,
        category: (data as any).category || undefined,
        location: data.location || undefined,
        bio: data.bio || undefined,
      } as any;

      if (extras?.profileImageId && !extras.profileImageFile) {
        (payload as any).profileImageId = extras.profileImageId;
      }

      const created = await stakeholderService.createStakeholder(payload);

      // Step 2: Save influence scores
      if (scores && scores.length) {
        try {
          await upsertInfluenceScores('stakeholder', created.id, scores);
        } catch (e) {
          console.error('Failed to save influence scores', e);
          showWarning('Influence Scores', 'Stakeholder created but influence scores could not be saved.');
        }
      }

      // Step 3: Upload profile image
      if (extras?.profileImageFile) {
        try {
          const asset = await mediaService.uploadFile(extras.profileImageFile, { stakeholderId: created.id });
          await stakeholderService.updateStakeholder(created.id, { profileImageId: asset.id } as any);
        } catch (e) {
          console.error('Failed to upload profile image', e);
          showWarning('Profile Image', 'Stakeholder created but profile image could not be uploaded.');
        }
      }

      // Step 4: Create assignment
      if (assignment?.organizationId && assignment.role) {
        const startIso = assignment.startDate ? new Date(assignment.startDate).toISOString() : new Date().toISOString();
        const endIso = assignment.isCurrent ? undefined : (assignment.endDate ? new Date(assignment.endDate).toISOString() : undefined);
        try {
          await assignmentService.create({
            stakeholderId: created.id,
            organizationId: assignment.organizationId,
            role: assignment.role,
            department: assignment.department || undefined,
            startDate: startIso,
            endDate: endIso,
            isCurrent: assignment.isCurrent ?? !endIso,
          });
        } catch (e) {
          console.error('Failed to create assignment', e);
          showWarning('Assignment', 'Stakeholder created but could not be linked to organization.');
        }
      }

      showSuccess('Stakeholder Created', `${created.firstName} ${created.lastName} has been added successfully.`);
      setIsDirty(false);
      navigate(`/stakeholders/${created.id}`);
    } catch (error: any) {
      console.error('Failed to create stakeholder', error);
      showError('Creation Failed', error.response?.data?.message || error.message || 'An unexpected error occurred.');
    } finally {
      setSubmitting(false);
    }
  }, [scores, showSuccess, showWarning, showError, navigate]);

  // Handle create with duplicate check
  const handleCreate = async (
    data: Partial<CreateStakeholderDto>,
    assignment?: any,
    extras?: any,
  ) => {
    // Check for duplicates first (non-blocking)
    try {
      const matches = await businessCardService.matchStakeholder({
        firstName: (data.firstName ?? '').trim(),
        lastName: (data.lastName ?? '').trim(),
        professionalEmail: (data as any).professionalEmail || undefined,
        personalEmail: (data as any).personalEmail || undefined,
        professionalPhone: (data as any).professionalPhone || undefined,
        personalPhone: (data as any).personalPhone || undefined,
      });

      if (matches.length > 0) {
        // Show duplicate review, store pending data
        setDuplicateMatches(matches);
        setPendingCreateData({ data, assignment, extras });
        setShowDuplicateReview(true);
        return;
      }
    } catch {
      // Non-blocking: proceed with creation on failure
    }

    await executeCreate(data, assignment, extras);
  };

  // Handle "Skip — Create New" from duplicate review
  const handleDuplicateSkip = useCallback(() => {
    setShowDuplicateReview(false);
    if (pendingCreateData) {
      executeCreate(pendingCreateData.data, pendingCreateData.assignment, pendingCreateData.extras);
    }
  }, [pendingCreateData, executeCreate]);

  // Handle merge from duplicate review (during manual creation)
  const handleDuplicateMerge = useCallback(async (stakeholderId: string, mergedData: Record<string, any>) => {
    try {
      await stakeholderService.updateStakeholder(stakeholderId, mergedData as any);
      showSuccess('Stakeholder Merged', 'Data has been merged with the existing stakeholder.');
      setShowDuplicateReview(false);
      setIsDirty(false);
      navigate(`/stakeholders/${stakeholderId}`);
    } catch (error: any) {
      showError('Merge Failed', error.response?.data?.message || error.message || 'Failed to merge stakeholder data.');
    }
  }, [showSuccess, showError, navigate]);

  // Handle merge from business card import flow
  const handleImportMerge = useCallback(async (stakeholderId: string, mergedData: Record<string, any>) => {
    try {
      await stakeholderService.updateStakeholder(stakeholderId, mergedData as any);
      showSuccess('Stakeholder Merged', 'Business card data has been merged with the existing stakeholder.');
      setShowImport(false);
      setIsDirty(false);
      navigate(`/stakeholders/${stakeholderId}`);
    } catch (error: any) {
      showError('Merge Failed', error.response?.data?.message || error.message || 'Failed to merge stakeholder data.');
    }
  }, [showSuccess, showError, navigate]);

  const handleCancel = () => {
    if (isDirty) {
      if (!window.confirm('You have unsaved changes. Are you sure you want to leave?')) return;
    }
    navigate('/stakeholders');
  };

  return (
    <DashboardLayout>
      <Page className="pb-20 sm:pb-0">
        {/* Back goes through handleCancel so unsaved changes are confirmed */}
        <button
          type="button"
          onClick={handleCancel}
          className="-ml-1 mb-2 inline-flex h-7 items-center gap-0.5 rounded-full pl-0.5 pr-2 text-[13px] text-neutral-500 transition-colors hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600/40"
        >
          <ChevronLeft className="h-4 w-4" strokeWidth={1.75} />
          Stakeholders
        </button>
        <PageHeader
          title="New stakeholder"
          actions={
            <Button size="sm" variant="secondary" onClick={() => setShowImport(true)}>
              <ScanLine className="h-4 w-4" strokeWidth={1.75} />
              Scan card
            </Button>
          }
        />

        {/* Pre-fill notice */}
        {showPrefillBanner && prefillData && (
          <div className="mb-4 flex animate-fade-up items-center gap-2.5 rounded-xl bg-neutral-100 py-1.5 pl-3.5 pr-1.5 text-[13px] text-ink">
            <ScanLine className="h-4 w-4 flex-shrink-0 text-neutral-500" strokeWidth={1.75} />
            <span className="min-w-0 flex-1 truncate">Filled from business card</span>
            <IconButton label="Dismiss" size="sm" onClick={() => setShowPrefillBanner(false)}>
              <X className="h-4 w-4" strokeWidth={1.75} />
            </IconButton>
          </div>
        )}

        {/* Form */}
        <StakeholderForm
          prefillData={formPrefill}
          includeOrganizationFields
          onSave={(s, a, extras) => handleCreate(s as Partial<CreateStakeholderDto>, a, extras)}
          isLoading={submitting}
          onInfluenceScoresChange={setScores}
          onCancel={handleCancel}
          onDirtyChange={setIsDirty}
        />
      </Page>

      {/* Business Card Import Modal */}
      {showImport && (
        <BusinessCardImport
          onImport={handleImport}
          onMerge={handleImportMerge}
          onClose={() => setShowImport(false)}
        />
      )}

      {/* Duplicate Review Sheet (for manual creation) */}
      {showDuplicateReview && pendingCreateData && (
        <DuplicateReviewSheet
          open={showDuplicateReview}
          onClose={() => setShowDuplicateReview(false)}
          matches={duplicateMatches}
          newData={pendingCreateData.data as Record<string, any>}
          onSkip={handleDuplicateSkip}
          onMerge={handleDuplicateMerge}
        />
      )}
    </DashboardLayout>
  );
};
